import React from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useGetOpsJobs, UserRole } from "@taxassist/api-client";

import { useAuth } from "../contexts/AuthContext";
import { StatusBadge } from "./jobs/StatusBadge";

import { getJobEtaDisplay, sortJobsBySubmittedAt } from "../utils/jobUtils";
import { formatDate } from "../utils/dateUtils";

interface MyJobsQueueProps {
  onJobSelected?: (jobId: string, firmId: string) => void;
  selectedJobId?: string | null;
  context?: "ops" | "client";
}

export const MyJobsQueue: React.FC<MyJobsQueueProps> = ({
  onJobSelected,
  selectedJobId,
  context = "client",
}) => {
  const { user, isAuthenticated } = useAuth();

  // Check if user has access
  const hasAccess =
    user?.role === UserRole.admin || user?.role === UserRole.reviewer;

  // Query parameters for all jobs (filter assigned jobs on frontend)
  const queryParams = {
    page: 1,
    limit: 100, // Get a reasonable number of jobs
  };

  const {
    data: jobsResponse,
    isLoading,
    error,
  } = useGetOpsJobs(queryParams, {
    query: {
      enabled: isAuthenticated && hasAccess && !!user?.userId,
      refetchInterval: 60000, // Auto-refresh every minute
      staleTime: 30000, // Consider data stale after 30 seconds
      queryKey: ["ops-jobs", "my-worklist", user?.userId],
    },
  });

  const allJobs = jobsResponse?.jobs || [];

  // Debug logging
  console.log("MyJobsQueue - Current user ID:", user?.userId);
  console.log("MyJobsQueue - All jobs:", allJobs.length);
  if (allJobs.length > 0) {
    console.log(
      "MyJobsQueue - Sample job assignedReviewer:",
      allJobs[0].assignedReviewer,
    );
  }

  // Filter to show only jobs assigned to the current user
  const filteredJobs = allJobs.filter((job) => job.assignedReviewer === user?.userId);

  // Sort jobs by submittedAt timestamp (ascending - oldest first)
  const sortedJobs = sortJobsBySubmittedAt(filteredJobs, 'oldest');

  console.log("MyJobsQueue - Filtered jobs for user:", sortedJobs.length);

  // Show access error for unauthorized users
  if (!isAuthenticated || !hasAccess) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Unauthorized - You need REVIEWER or ADMIN access to view this page
        </Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load jobs: {error.message}</Alert>
      </Box>
    );
  }

  if (sortedJobs.length === 0) {
    return (
      <Box p={3}>
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="small" />}
          sx={{
            bgcolor: "info.light",
            color: "info.dark",
            borderLeft: "4px solid",
            borderColor: "info.main",
            "& .MuiAlert-icon": {
              color: "info.main",
            },
          }}
        >
          No jobs assigned to you at the moment. Check back later or grab work
          from the Ops queue.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <TableContainer component={Paper}>
        <Table
          size="small"
          sx={{
            "& .MuiTableCell-root": {
              fontSize: "1rem", // same as body1 (16px by default)
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Firm</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date Due</TableCell>
              <TableCell>Date Submitted</TableCell>
              <TableCell>Assigned</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedJobs.map((job) => (
              <TableRow
                key={job.jobId}
                hover
                onClick={() => onJobSelected?.(job.jobId, job.firmId)}
                sx={{
                  cursor: onJobSelected ? "pointer" : "default",
                  backgroundColor:
                    selectedJobId === job.jobId ? "#EDF7FF" : "inherit",
                  "&:hover": {
                    backgroundColor:
                      selectedJobId === job.jobId ? "#E3F2FD" : "#F5F5F5",
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {job.clientName || "Unknown Client"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {job.clientNumber || job.jobId.substring(0, 8)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {job.firmName || "Unknown Firm"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={job.status as any}
                    statusMapping={null}
                    job={job as any}
                    context={context}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getJobEtaDisplay(job)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {job.submittedAt ? formatDate(job.submittedAt, 'MM/dd/yy hh:mm a') : "N/A"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {job.assignedAt ? formatDate(job.assignedAt, 'MM/dd/yy hh:mm a') : "N/A"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
